import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Gift, Loader2, X } from "lucide-react";
import { useDispatch } from "react-redux";
import { formatCash } from "../../utils/operatorConsoleHelpers";
import { rewardBettor } from "../../redux/operatorConsoleSlice";

const BettorRewardModal = ({
  open,
  onClose,
  houseId,
  houseName,
  bettor,
  operatorUserId,
  onSuccess,
  onSubmittingChange,
}) => {
  const dispatch = useDispatch();
  const [currency, setCurrency] = useState("CASH");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    onSubmittingChange?.(submitting);
  }, [submitting, onSubmittingChange]);

  useEffect(() => {
    if (!open) return undefined;
    setCurrency("CASH");
    setAmount("");
    setReason("");
    setSubmitting(false);
    setError(null);

    const onKey = (e) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, submitting]);

  if (!open || !bettor) return null;

  const handleClose = () => {
    if (!submitting) onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!houseId || !operatorUserId || !bettor?.userId) {
      setError("Missing session info. Sign in again and retry.");
      return;
    }

    const n = Number(String(amount).replace(/,/g, "").trim());
    if (!Number.isFinite(n) || n <= 0) {
      setError("Enter a positive amount.");
      return;
    }

    const reasonTrim = reason.trim().slice(0, 280) || "Thanks for playing";

    setSubmitting(true);
    try {
      const result = await dispatch(
        rewardBettor({
          houseId,
          bettorUserId: String(bettor.userId),
          operatorUserId,
          currency,
          amount: n,
          reason: reasonTrim,
        })
      ).unwrap();

      onSuccess?.({
        currency,
        amount: n,
        bettorName: bettor.username,
        message: result?.message,
      });
      onClose();
    } catch (err) {
      setError(String(err || "Reward failed. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div className="bd-reward-modal-root" role="dialog" aria-modal="true" aria-labelledby="bd-reward-title">
      <button
        type="button"
        className="bd-reward-modal-backdrop"
        aria-label="Close"
        onClick={handleClose}
        disabled={submitting}
      />
      <div
        className={`bd-reward-modal-sheet ${submitting ? "bd-reward-modal-sheet-busy" : ""}`}
        aria-busy={submitting}
      >
        {submitting ? (
          <div className="bd-reward-modal-busy" aria-live="polite">
            <Loader2 size={28} className="bd-reward-modal-busy-spin" aria-hidden />
            <strong>Sending reward…</strong>
            <span>Waiting for server confirmation</span>
          </div>
        ) : null}
        <header className="bd-reward-modal-head">
          <div>
            <p className="bd-reward-modal-eyebrow">{houseName || "Bet house"}</p>
            <h2 id="bd-reward-title" className="bd-reward-modal-title">
              Send reward
            </h2>
            <p className="bd-reward-modal-sub">
              To <strong>{bettor.username || "Bettor"}</strong>
            </p>
          </div>
          <button
            type="button"
            className="bd-reward-modal-close"
            onClick={handleClose}
            disabled={submitting}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </header>

        <form className="bd-reward-modal-body" onSubmit={handleSubmit}>
          <div className="bd-reward-tabs" role="tablist" aria-label="Reward currency">
            <button
              type="button"
              role="tab"
              aria-selected={currency === "CASH"}
              className={`bd-reward-tab ${currency === "CASH" ? "bd-reward-tab-active" : ""}`}
              onClick={() => setCurrency("CASH")}
              disabled={submitting}
            >
              Cash
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={currency === "COIN"}
              className={`bd-reward-tab ${currency === "COIN" ? "bd-reward-tab-active" : ""}`}
              onClick={() => setCurrency("COIN")}
              disabled={submitting}
            >
              Coins
            </button>
          </div>

          <label className="bd-reward-field">
            <span>Amount</span>
            <input
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={currency === "COIN" ? "e.g. 500" : `e.g. ${formatCash(1000, { compact: true })}`}
              disabled={submitting}
              autoFocus
            />
          </label>

          <label className="bd-reward-field">
            <span>Note (optional)</span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="VIP bonus, milestone, loyalty thank-you…"
              maxLength={280}
              rows={3}
              disabled={submitting}
            />
          </label>

          <p className="bd-reward-hint">
            {currency === "COIN"
              ? "Coins credit instantly to the bettor’s wallet."
              : "Cash credits instantly and appears in their transaction history."}
          </p>

          {error ? (
            <p className="bd-reward-error" role="alert">
              {error}
            </p>
          ) : null}

          <div className="bd-reward-actions">
            <button type="button" className="bd-reward-cancel" onClick={handleClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="bd-reward-submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 size={14} className="bd-reward-modal-busy-spin" aria-hidden />
                  Sending…
                </>
              ) : (
                <>
                  <Gift size={14} />
                  Send reward
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default BettorRewardModal;
